import React, { useState } from 'react';
import { Bug } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { bugReportsAPI } from '../lib/api';

const BugReportButton = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the bug');
      return;
    }

    try {
      setSubmitting(true);
      
      // Capture context
      const bugReport = {
        description: description.trim(),
        priority,
        page_url: window.location.href,
        browser_info: navigator.userAgent,
      };

      await bugReportsAPI.create(bugReport);
      
      toast.success('Bug report submitted! Thank you for your feedback.');
      setOpen(false);
      setDescription('');
      setPriority('medium');
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      toast.error('Failed to submit bug report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when dialog closes
      setDescription('');
      setPriority('medium');
    }
  };

  return (
    <>
      {/* Floating Bug Report Button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-20 right-6 h-12 w-12 rounded-full shadow-lg border-border/50 bg-card hover:bg-card/80 hover:border-red-500/50 z-50"
        onClick={() => setOpen(true)}
        data-testid="bug-report-button"
      >
        <Bug className="h-5 w-5 text-red-400" />
      </Button>

      {/* Bug Report Dialog */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-400" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Found something that's not working right? Let us know and we'll fix it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="description">What happened?</Label>
              <Textarea
                id="description"
                placeholder="Describe the bug you encountered. What were you trying to do? What did you expect to happen? What actually happened?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[120px] resize-none"
                data-testid="bug-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="priority" data-testid="bug-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-slate-400" />
                      Low - Minor issue, not blocking
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-yellow-400" />
                      Medium - Annoying but can work around
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-orange-400" />
                      High - Significantly impacts work
                    </span>
                  </SelectItem>
                  <SelectItem value="critical">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      Critical - Can't do my job
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/30 rounded-md p-3">
              <p className="font-medium mb-1">Automatically captured:</p>
              <p>• Current page URL</p>
              <p>• Browser information</p>
              <p>• Your account details</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !description.trim()}
              className="bg-red-500 hover:bg-red-600"
              data-testid="submit-bug-report"
            >
              {submitting ? 'Submitting...' : 'Submit Bug Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportButton;
